import { styled } from "@mui/material/styles";
import React from "react";
import { Spinner } from "./Feedback";
import { ChevronLeftIcon } from "./icons";
import { focusRing, pressable } from "./tokens";
const SECONDARY = {
    background: "var(--j-surface)",
    borderColor: "var(--j-line-strong)",
    color: "var(--j-ink)",
    "&:hover:not(:disabled)": {
        background: "var(--j-hover)"
    },
    "&:disabled:not([aria-busy=true])": {
        color: "var(--j-ink-3)",
        background: "var(--j-surface)",
        borderColor: "var(--j-line)",
        cursor: "default"
    }
};
const VARIANT_STYLE = {
    primary: {
        background: "var(--j-accent)",
        color: "var(--j-on-accent)",
        "&:hover:not(:disabled)": {
            background: "var(--j-accent-hover)"
        }
    },
    tonal: SECONDARY,
    outline: SECONDARY,
    ghost: {
        background: "none",
        color: "var(--j-ink-2)",
        "&:hover:not(:disabled)": {
            background: "var(--j-hover)",
            color: "var(--j-ink)"
        },
        "&:disabled:not([aria-busy=true])": {
            color: "var(--j-ink-3)",
            background: "none",
            cursor: "default"
        }
    },
    danger: {
        background: "var(--j-err)",
        color: "var(--j-on-accent)",
        "&:hover:not(:disabled)": {
            background: "color-mix(in srgb, var(--j-err) 92%, var(--j-ink))"
        }
    }
};
const SIZE_STYLE = {
    sm: {
        height: "var(--j-btn-h)"
    },
    md: {
        height: "var(--j-btn-h)"
    },
    lg: {
        height: "var(--j-btn-h-lg)"
    }
};
const ButtonRoot = styled("button", {
    shouldForwardProp: (prop)=>prop !== "variant" && prop !== "size" && prop !== "hasStart" && prop !== "hasEnd"
})(({ variant, size, hasStart, hasEnd })=>({
        ...focusRing,
        ...pressable,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "var(--j-btn-gap)",
        flexShrink: 0,
        padding: `0 ${hasEnd ? "var(--j-btn-px-icon)" : "var(--j-btn-px)"} 0 ${hasStart ? "var(--j-btn-px-icon)" : "var(--j-btn-px)"}`,
        border: "1px solid transparent",
        borderRadius: "var(--j-r-sm)",
        fontSize: "var(--j-fs-md)",
        fontWeight: 500,
        lineHeight: 1,
        whiteSpace: "nowrap",
        cursor: "pointer",
        "&:disabled:not([aria-busy=true])": {
            color: "var(--j-ink-3)",
            background: "var(--j-well)",
            borderColor: "transparent",
            cursor: "default"
        },
        "&[aria-busy=true]": {
            cursor: "default"
        },
        ...VARIANT_STYLE[variant],
        ...SIZE_STYLE[size]
    }));
export const Button = /*#__PURE__*/ React.forwardRef(function Button({ variant = "primary", size = "md", loading = false, startIcon, endIcon, children, disabled, type = "button", ...rest }, ref) {
    const isInverse = variant === "primary" || variant === "danger";
    return /*#__PURE__*/ React.createElement(ButtonRoot, {
        ref: ref,
        variant: variant,
        size: size,
        hasStart: loading || !!startIcon,
        hasEnd: !!endIcon,
        type: type,
        disabled: disabled || loading,
        "aria-busy": loading || undefined,
        ...rest
    }, loading ? /*#__PURE__*/ React.createElement(Spinner, {
        size: "sm",
        tone: isInverse ? "inverse" : "primary"
    }) : startIcon, children, endIcon);
});
const IconButtonRoot = styled("button", {
    shouldForwardProp: (prop)=>prop !== "size"
})(({ size })=>({
        ...focusRing,
        ...pressable,
        width: size === "sm" ? "var(--j-btn-h)" : "var(--j-btn-h-lg)",
        height: size === "sm" ? "var(--j-btn-h)" : "var(--j-btn-h-lg)",
        flexShrink: 0,
        padding: 0,
        border: 0,
        borderRadius: "var(--j-r-sm)",
        background: "none",
        color: "var(--j-ink-2)",
        display: "grid",
        placeItems: "center",
        cursor: "pointer",
        "&:hover:not(:disabled)": {
            background: "var(--j-hover)",
            color: "var(--j-ink)"
        },
        "&[aria-expanded=true], &[aria-pressed=true], &[aria-expanded=true]:hover, &[aria-pressed=true]:hover": {
            background: "var(--j-accent-soft)",
            color: "var(--j-accent-ink)"
        },
        "&:disabled": {
            color: "var(--j-ink-3)",
            cursor: "default"
        }
    }));
export const IconButton = /*#__PURE__*/ React.forwardRef(function IconButton({ size = "md", type = "button", children, ...rest }, ref) {
    return /*#__PURE__*/ React.createElement(IconButtonRoot, {
        ref: ref,
        size: size,
        type: type,
        ...rest
    }, children);
});
const ghost = {
    ...focusRing,
    ...pressable,
    height: "var(--j-btn-h)",
    display: "inline-flex",
    alignItems: "center",
    gap: "var(--j-btn-gap)",
    border: 0,
    borderRadius: "var(--j-r-sm)",
    background: "none",
    fontSize: "var(--j-fs-md)",
    fontWeight: 500,
    lineHeight: 1,
    cursor: "pointer",
    whiteSpace: "nowrap",
    "&:hover:not(:disabled)": {
        background: "var(--j-hover)",
        color: "var(--j-ink)"
    },
    "&:disabled": {
        color: "var(--j-ink-3)",
        cursor: "default"
    }
};
const BackRoot = styled("button")({
    ...ghost,
    padding: "0 var(--j-btn-px) 0 var(--j-btn-px-icon)",
    color: "var(--j-ink-2)",
    minWidth: 0,
    "& > span": {
        overflow: "hidden",
        textOverflow: "ellipsis"
    }
});
export const BackButton = /*#__PURE__*/ React.forwardRef(function BackButton({ children, type = "button", ...rest }, ref) {
    return /*#__PURE__*/ React.createElement(BackRoot, {
        ref: ref,
        type: type,
        ...rest
    }, /*#__PURE__*/ React.createElement(ChevronLeftIcon, null), /*#__PURE__*/ React.createElement("span", null, children));
});
const TextButtonRoot = styled("button", {
    shouldForwardProp: (prop)=>prop !== "tone"
})(({ tone })=>({
        ...ghost,
        padding: "0 var(--j-btn-px)",
        color: tone === "primary" ? "var(--j-ink)" : "var(--j-ink-2)"
    }));
export function TextButton({ tone = "primary", type = "button", ...rest }) {
    return /*#__PURE__*/ React.createElement(TextButtonRoot, {
        tone: tone,
        type: type,
        ...rest
    });
}
